import React, { useMemo, useState } from "react";
import { useOppBotMutations } from "../hooks/useOppBotApi";
import { curateLabel, picklistsFor, recordTypeMeta, recordTypeNameForId } from "../utils";

// Inline editor for a needs_review plan. Only the whitelisted fields (server
// EDITABLE_FIELDS) are sent; contacts / account can be dropped from the plan.
export default function ReviewForm({ doc, meta, onDone }) {
  const { approve, reject } = useOppBotMutations();
  const actions = (doc.plan && doc.plan.actions) || [];
  const oppAction = actions.find((a) => ["create_opportunity", "update_opportunity", "close_opportunity"].includes(a.type));
  const accountAction = actions.find((a) => a.type === "create_account");
  const contactActions = actions.filter((a) => a.type === "create_contact");
  const isUpdate = oppAction && oppAction.type === "update_opportunity";
  const isClose = oppAction && oppAction.type === "close_opportunity";
  const closeAlreadyDone = isClose && oppAction.status === "skipped";
  const isRequest = doc.parsed && doc.parsed.mode === "request";

  const [opp, setOpp] = useState(() => ({ ...((oppAction && oppAction.fields) || {}) }));
  const [accountName, setAccountName] = useState((accountAction && accountAction.fields && accountAction.fields.Name) || "");
  const [removed, setRemoved] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [advanced, setAdvanced] = useState(false);

  const owners = (meta && meta.owners) || [];
  const recordTypes = (meta && meta.picklists && meta.picklists.recordTypes) || [];
  const events = (meta && meta.summitEvents) || [];
  const guide = (meta && meta.recordTypeGuide) || {};

  // stage / lead source / type options depend on the selected record type (or the existing opp's type)
  const existingOpp = isUpdate || isClose ? ((doc.context && doc.context.openOpportunities) || []).find((o) => o.sfId === oppAction.targetSfId) : null;
  const rtName = isUpdate || isClose ? (existingOpp && existingOpp.recordTypeName) || "" : recordTypeNameForId(meta, opp.RecordTypeId);
  const pk = picklistsFor(meta, rtName);
  // closed stages are only offered through the close-out form (Outcome toggle), never in create/update
  const stages = isClose ? pk.stages : pk.stages.filter((s) => !s.closed && !/^closed\b/i.test(s.value));
  const leadSources = pk.leadSources;
  const isCurate = /curate/i.test(rtName);

  // close-out: which stage means won / lost for this record type (server marks closed/won on stages; fall back to names)
  const closedStage = (won) => {
    const byFlag = stages.find((s) => s.closed && !!s.won === won);
    if (byFlag) return byFlag.value;
    const byName = stages.find((s) => new RegExp(won ? "^closed\\s*won" : "^closed\\s*lost", "i").test(s.value));
    return byName ? byName.value : won ? "Closed Won" : "Closed Lost";
  };
  const closeOutcome = isClose ? (/won/i.test(opp.StageName || "") ? "won" : "lost") : null;
  const changeOutcome = (won) =>
    setOpp((prev) => {
      const next = { ...prev, StageName: closedStage(won) };
      if (typeof next.Description === "string") next.Description = next.Description.replace(/Closed (Lost|Won)/g, won ? "Closed Won" : "Closed Lost");
      if (won) {
        delete next.Loss_Reason__c;
        if (next.Amount === undefined) next.Amount = (existingOpp && existingOpp.amount) ?? "";
      } else {
        delete next.of_Custom_Events__c;
        if (next.Loss_Reason__c === undefined) next.Loss_Reason__c = (doc.ai && doc.ai.closeOut && doc.ai.closeOut.lossReason) || "";
      }
      return next;
    });
  const closeIssues = [];
  if (isClose && !closeAlreadyDone) {
    if (closeOutcome === "won" && (opp.Amount === "" || opp.Amount === null || opp.Amount === undefined)) closeIssues.push("Amount is required for a Closed Won.");
    if (closeOutcome === "won" && isCurate && (opp.of_Custom_Events__c === "" || opp.of_Custom_Events__c === null || opp.of_Custom_Events__c === undefined)) closeIssues.push("Salesforce requires # of Curate events before a Curate opportunity can be Closed Won.");
    if (closeOutcome === "lost" && !String(opp.Loss_Reason__c || "").trim()) closeIssues.push("Add a loss reason - the team records why every deal was lost.");
  }

  // switching record type: drop values that are not valid for the new one
  const changeRecordType = (e) => {
    const id = e.target.value;
    const nextPk = picklistsFor(meta, recordTypeNameForId(meta, id));
    setOpp((prev) => {
      const next = { ...prev, RecordTypeId: id };
      if (next.StageName && !nextPk.stages.some((s) => s.value === next.StageName)) next.StageName = "";
      if (next.LeadSource && !nextPk.leadSources.includes(next.LeadSource)) next.LeadSource = "";
      if (next.Type && !nextPk.types.includes(next.Type)) next.Type = "";
      if (!/curate/i.test(recordTypeNameForId(meta, id))) {
        next.Curate_Category__c = "";
        next.P_L_Type__c = "";
      }
      return next;
    });
  };
  const changeCurateCategory = (e) => {
    const cat = e.target.value;
    setOpp((prev) => ({
      ...prev,
      Curate_Category__c: cat,
      // keep P&L type in step with the category unless the user already picked one
      P_L_Type__c: cat ? pk.plTypes.find((v) => (/2/.test(cat) ? /dinner/i.test(v) : /conference/i.test(v))) || prev.P_L_Type__c || "" : prev.P_L_Type__c,
    }));
  };

  const dirty = useMemo(() => {
    if (!oppAction) return false;
    const orig = oppAction.fields || {};
    const keys = new Set([...Object.keys(orig), ...Object.keys(opp)]);
    for (const k of keys) if (String(orig[k] ?? "") !== String(opp[k] ?? "")) return true;
    if (accountAction && accountName !== (accountAction.fields.Name || "")) return true;
    return Object.values(removed).some(Boolean);
  }, [opp, oppAction, accountAction, accountName, removed]);

  const set = (k) => (e) => setOpp((prev) => ({ ...prev, [k]: e.target.value }));

  const submitApprove = async () => {
    setBusy("approve");
    setError("");
    try {
      const edits = [];
      if (oppAction && !closeAlreadyDone) {
        const fields = {};
        const orig = oppAction.fields || {};
        for (const [k, v] of Object.entries(opp)) if (String(orig[k] ?? "") !== String(v ?? "")) fields[k] = v;
        // fields dropped when switching won <-> lost are sent as empty so the server clears them
        for (const k of Object.keys(orig)) if (!(k in opp)) fields[k] = "";
        if (Object.keys(fields).length) edits.push({ key: oppAction.key, fields });
      }
      if (accountAction && accountAction.status !== "skipped" && accountName !== accountAction.fields.Name) edits.push({ key: accountAction.key, fields: { Name: accountName } });
      for (const [key, r] of Object.entries(removed)) if (r) edits.push({ key, remove: true });
      // dropping a contact also drops its role
      for (const [key, r] of Object.entries(removed)) if (r && actions.some((a) => a.key === `role:${key}`)) edits.push({ key: `role:${key}`, remove: true });
      await approve(doc._id, { actions: edits, note });
      onDone && onDone();
    } catch (e) {
      setError(e.message || "Approve failed");
    } finally {
      setBusy("");
    }
  };

  const submitReject = async () => {
    setBusy("reject");
    setError("");
    try {
      await reject(doc._id, note);
      onDone && onDone();
    } catch (e) {
      setError(e.message || "Reject failed");
    } finally {
      setBusy("");
    }
  };

  const canApprove = !!oppAction && ["new_opportunity", "update_opportunity", "close_opportunity"].includes(doc.ai && doc.ai.intent);

  return (
    <div className="ob-review">
      <div className="ob-eyebrow">Review before writing to Salesforce</div>
      {!canApprove && (
        <div className="ob-notice ob-notice--warning" style={{ marginBottom: 10 }}>
          {doc.ai && doc.ai.intent === "close_opportunity"
            ? "The AI read this as a close-out but could not find an opportunity to close, so nothing can be written. Forward it again naming the opportunity in your notes, or close it by hand in Salesforce."
            : isRequest
            ? "The AI did not propose a Salesforce action for this request, so it can only be rejected or reprocessed. Reply with more detail (who, what they want, how it came in) and send again."
            : "The AI did not propose a Salesforce action for this email, so it can only be rejected or reprocessed. Add clearer notes at the top and forward again if it should have created an opportunity."}
        </div>
      )}
      {doc.plan && doc.plan.gateReason && (
        <div className="ob-notice ob-notice--info" style={{ marginBottom: 12 }}>
          Held because: {doc.plan.gateReason}
        </div>
      )}

      {isClose && closeAlreadyDone && (
        <div className="ob-notice ob-notice--info" style={{ marginBottom: 12 }}>
          <b>{(existingOpp && existingOpp.name) || "The opportunity"}</b> is already {(existingOpp && existingOpp.stageName) || "closed"}. Approving only logs this email as a Task on it; nothing else changes.
        </div>
      )}

      {isClose && !closeAlreadyDone && (
        <div className="ob-review__grid">
          <div className="ob-notice span-2" style={{ marginBottom: 2 }}>
            Closing the existing <b>{rtName || "Opportunity"}</b> opportunity <b>{(existingOpp && existingOpp.name) || oppAction.targetSfId}</b>
            {existingOpp && existingOpp.stageName ? ` (currently ${existingOpp.stageName})` : ""}.
          </div>
          <div className="ob-field span-2">
            <label>Outcome</label>
            <div className="ob-segmented" role="radiogroup" aria-label="Outcome">
              <button type="button" role="radio" aria-checked={closeOutcome === "lost"} className={`ob-segmented__btn is-lost ${closeOutcome === "lost" ? "is-active" : ""}`} onClick={() => changeOutcome(false)}>
                Closed Lost
              </button>
              <button type="button" role="radio" aria-checked={closeOutcome === "won"} className={`ob-segmented__btn is-won ${closeOutcome === "won" ? "is-active" : ""}`} onClick={() => changeOutcome(true)}>
                Closed Won
              </button>
            </div>
            <span className="ob-field__hint">Stage will be set to "{opp.StageName}".</span>
          </div>
          <div className="ob-field">
            <label>Close date</label>
            <input type="date" value={opp.CloseDate || ""} onChange={set("CloseDate")} />
            <span className="ob-field__hint">The day the decision was made.</span>
          </div>
          <div className="ob-field">
            <label>Amount (USD){closeOutcome === "won" ? " · required" : ""}</label>
            <input type="number" min="0" step="1000" value={opp.Amount ?? ""} onChange={set("Amount")} placeholder={closeOutcome === "won" ? "e.g. 25000" : "optional"} />
          </div>
          {closeOutcome === "won" && isCurate && (
            <div className="ob-field">
              <label># of Curate events · required</label>
              <input type="number" min="0" step="1" value={opp.of_Custom_Events__c ?? ""} onChange={set("of_Custom_Events__c")} placeholder="e.g. 2" />
              <span className="ob-field__hint">Salesforce blocks Curate wins without this.</span>
            </div>
          )}
          {closeOutcome === "lost" && (
            <div className="ob-field span-2">
              <label>Loss reason (written to the opportunity)</label>
              <textarea value={opp.Loss_Reason__c || ""} onChange={set("Loss_Reason__c")} rows={5} />
            </div>
          )}
          <div className="ob-field span-2">
            <label>Description (appended to the existing description)</label>
            <textarea value={opp.Description || ""} onChange={set("Description")} />
          </div>
          {closeIssues.length > 0 && (
            <div className="ob-notice ob-notice--warning span-2">
              {closeIssues.map((m) => (
                <div key={m}>{m}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {oppAction && !isClose && (
        <div className="ob-review__grid">
          {!isUpdate && (
            <div className="ob-field span-2">
              <label>Business line (record type)</label>
              <div className="ob-segmented" role="radiogroup" aria-label="Record type">
                {recordTypes.map((r) => {
                  const m = recordTypeMeta(r.name);
                  const active = opp.RecordTypeId === r.recordTypeId;
                  return (
                    <button
                      key={r.recordTypeId}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`ob-segmented__btn ${active ? "is-active" : ""}`}
                      onClick={() => changeRecordType({ target: { value: r.recordTypeId } })}
                      title={(guide[r.name] && (guide[r.name].summary || guide[r.name].when)) || m.blurb}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
              {rtName && <span className="ob-field__hint">{recordTypeMeta(rtName).blurb}</span>}
            </div>
          )}
          {isUpdate && existingOpp && (
            <div className="ob-notice span-2" style={{ marginBottom: 2 }}>
              Updating the existing <b>{existingOpp.recordTypeName || "Opportunity"}</b> opportunity <b>{existingOpp.name}</b>
              {existingOpp.curateCategory ? ` (${curateLabel(existingOpp.curateCategory)})` : ""}. Stage options below are the ones valid for that record type.
            </div>
          )}
          {!isUpdate && (
            <div className="ob-field span-2">
              <label>Opportunity name</label>
              <input value={opp.Name || ""} onChange={set("Name")} />
            </div>
          )}
          <div className="ob-field">
            <label>Stage</label>
            <select value={opp.StageName || ""} onChange={set("StageName")}>
              {isUpdate ? <option value="">(leave unchanged)</option> : <option value="">(choose)</option>}
              {stages.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.value}
                </option>
              ))}
              {opp.StageName && !stages.some((s) => s.value === opp.StageName) && <option value={opp.StageName}>{opp.StageName} (not valid for this record type)</option>}
            </select>
          </div>
          <div className="ob-field">
            <label>Close date</label>
            <input type="date" value={opp.CloseDate || ""} onChange={set("CloseDate")} />
          </div>
          {!isUpdate && (
            <>
              <div className="ob-field">
                <label>Owner</label>
                <select value={opp.OwnerId || ""} onChange={set("OwnerId")}>
                  <option value="">(default owner)</option>
                  {owners.map((o) => (
                    <option key={o.sfUserId} value={o.sfUserId}>
                      {o.name}
                      {o.team ? ` · ${o.team}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ob-field">
                <label>Lead source</label>
                <select value={opp.LeadSource || ""} onChange={set("LeadSource")}>
                  <option value="">(none)</option>
                  {leadSources.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                  {opp.LeadSource && !leadSources.includes(opp.LeadSource) && <option value={opp.LeadSource}>{opp.LeadSource} (not valid for this record type)</option>}
                </select>
              </div>
              {isCurate ? (
                <>
                  <div className="ob-field">
                    <label>Curate category</label>
                    <select value={opp.Curate_Category__c || ""} onChange={changeCurateCategory}>
                      <option value="">(none)</option>
                      {pk.curateCategories.map((v) => (
                        <option key={v} value={v}>
                          {curateLabel(v)}
                          {/1/.test(v) ? " · conferences, summits, large events" : /2/.test(v) ? " · dinners, lunches, recruitment-only" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="ob-field">
                    <label>P&amp;L type</label>
                    <select value={opp.P_L_Type__c || ""} onChange={set("P_L_Type__c")}>
                      <option value="">(none)</option>
                      {pk.plTypes.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <div className="ob-field">
                  <label>Summit event</label>
                  <select value={opp.Summit_Event__c || ""} onChange={set("Summit_Event__c")}>
                    <option value="">(none)</option>
                    {events.map((e) => (
                      <option key={e.sfId} value={e.sfId}>
                        {e.name}
                        {e.eventDate ? ` · ${e.eventDate}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="ob-field">
                <label>Type</label>
                <select value={opp.Type || ""} onChange={set("Type")}>
                  <option value="">(none)</option>
                  {pk.types.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className="ob-field">
            <label>Amount (USD)</label>
            <input type="number" min="0" step="1000" value={opp.Amount ?? ""} onChange={set("Amount")} placeholder="optional" />
          </div>
          <div className="ob-field">
            <label>Next step</label>
            <input value={opp.NextStep || ""} onChange={set("NextStep")} maxLength={255} />
          </div>
          <div className="ob-field span-2">
            <label>{isUpdate ? "Description (appended to the existing description)" : "Description"}</label>
            <textarea value={opp.Description || ""} onChange={set("Description")} />
          </div>
        </div>
      )}

      {(accountAction || contactActions.length > 0) && (
        <details className="ob-reasoning" open={advanced} onToggle={(e) => setAdvanced(e.target.open)}>
          <summary>Account and contacts</summary>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            {accountAction && accountAction.status !== "skipped" && (
              <div className="ob-field">
                <label>New account name</label>
                <input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
                <span className="ob-field__hint">A new Account will be created because none matched the prospect's domain.</span>
              </div>
            )}
            {accountAction && accountAction.status === "skipped" && (
              <div className="ob-notice">
                Using existing Account <b>{accountAction.fields.Name}</b>.
              </div>
            )}
            {contactActions.length > 0 && (
              <div className="ob-contact-toggles">
                {contactActions.map((c) => (
                  <label key={c.key} className={`ob-check ${removed[c.key] ? "is-off" : ""}`}>
                    <input type="checkbox" checked={!removed[c.key]} onChange={(e) => setRemoved((prev) => ({ ...prev, [c.key]: !e.target.checked }))} />
                    <span>
                      {c.status === "skipped" ? "Link existing contact " : "Create contact "}
                      <b>{c.fields.Name || `${c.fields.FirstName || ""} ${c.fields.LastName || ""}`.trim()}</b>
                      {c.fields.Email ? ` · ${c.fields.Email}` : c.status !== "skipped" ? " · no email known" : ""}
                      {c.fields.Title ? ` · ${c.fields.Title}` : ""}
                      {c.fields.LinkedIn__c ? (
                        <>
                          {" · "}
                          <a href={c.fields.LinkedIn__c} target="_blank" rel="noopener noreferrer">
                            LinkedIn
                          </a>
                        </>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </details>
      )}

      <div className="ob-field" style={{ marginTop: 12 }}>
        <label>Reviewer note (optional)</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you approved / rejected, for the audit trail" />
      </div>

      {error && (
        <div className="ob-notice ob-notice--danger" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}

      <div className="ob-review__footer">
        <span className="ob-muted" style={{ fontSize: 12.5 }}>
          {closeAlreadyDone ? "Approving logs the email on the closed opportunity." : dirty ? "Your edits will be applied instead of the AI's proposal." : "Approving writes the plan above to Salesforce."}
        </span>
        <div className="ob-review__btns">
          <button type="button" className="ob-btn ob-btn--danger" disabled={!!busy} onClick={submitReject}>
            {busy === "reject" ? "Rejecting…" : "Reject"}
          </button>
          <button type="button" className="ob-btn ob-btn--success" disabled={!!busy || !canApprove || closeIssues.length > 0} onClick={submitApprove} title={closeIssues.length ? closeIssues.join(" ") : undefined}>
            {busy === "approve" ? "Writing to Salesforce…" : closeAlreadyDone ? "Approve (log email)" : dirty ? "Approve with edits" : "Approve"}
          </button>
        </div>
      </div>
    </div>
  );
}
